/**
 *
 *  Organizations/Show/AdditionalQuestions/Form
 *
 */

import React, { useState } from 'react'

// NPM Libraries
import PropTypes from 'prop-types'
import styled from '@emotion/styled'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'

// GraphQL
import { gql, useMutation } from '@apollo/client'

// Components
import Modal from 'components/Modal'
import ResponsesSelector from '../ResponsesSelector'

// Utils
import { COLORS } from 'global-styles'
import TopicSelector from '../TopicSelector'

const CREATE_ADDITIONAL_QUESTION = gql`
	mutation createCampaignAdditionalQuestion(
		$organization_id: ID!
		$question: String!
		$topics: [String]
		$options: [String]
		$global: Boolean
	) {
		createCampaignAdditionalQuestion(
			input: {
				organization_id: $organization_id
				question: $question
				topics: $topics
				options: $options
				global: $global
			}
		) {
			campaign_additional_question {
				id
			}
		}
	}
`

const UPDATE_ADDITIONAL_QUESTION = gql`
	mutation updateCampaignAdditionalQuestion(
		$id: ID!
		$question: String!
		$topics: [String]
		$options: [String]
		$global: Boolean
	) {
		updateCampaignAdditionalQuestion(
			input: { id: $id, question: $question, topics: $topics, options: $options, global: $global }
		) {
			campaign_additional_question {
				id
			}
		}
	}
`

export const Field = styled.div`
	display: flex;
	flex-direction: column;
	margin: 15px 0;

	.label {
		font-size: 0.9rem;
		margin-bottom: 10px;
		position: relative;
		width: 100%;
		font-style: italic;

		&::before {
			content: '';
			position: absolute;
			right: 0px;
			top: 50%;
			height: 1px;
			background-color: ${COLORS.grey_e};
		}

		&.options::before {
			width: 68%;
		}

		&.topics::before {
			width: 87%;
		}
	}

	input {
		padding: 5px 10px;
		font-size: 0.95rem;

		&:focus {
			outline: none !important;
			box-shadow: none;
		}
	}

	&.error {
		.label {
			color: ${COLORS.danger};
		}

		input {
			outline-color: ${COLORS.danger} !important;
			border-color: ${COLORS.danger} !important;
		}

		.error-icon {
			visibility: visible !important;
		}

		.react-toggle-track {
			background-color: ${COLORS.danger} !important;
		}
	}
`

const QuestionField = styled(Field)`
	.field {
		margin-left: 20px;
		display: flex;
		justify-content: space-between;
		align-items: center;
		position: relative;

		.error-icon {
			visibility: hidden;
			position: absolute;
			top: 10px;
			right: 10px;
		}
	}
`

const ResponsesField = styled(Field)`
	width: 350px;

	.react-toggle {
		transform: scale(0.75);
	}
`

const TopicsField = styled(Field)`
	width: 350px;
`

const Divider = styled.div`
	height: 330px;
	width: 1px;
	background-color: ${COLORS.grey_e};
	margin: 10px 20px;
`

const initial_additional_question = {
	question: '',
	topics: [],
	global: false,
	options: [],
	freeform_response: true,
}

function AdditionalQuestionForm({ additional_question, handleClose, organization_id }) {
	const is_editing = !!additional_question?.id

	const [question, setQuestion] = useState(
		is_editing
			? { ...additional_question, freeform_response: additional_question.options.length === 0 }
			: initial_additional_question,
	)

	const [error, setError] = useState([])

	const [createQuestion, { loading: creating_question }] = useMutation(CREATE_ADDITIONAL_QUESTION, {
		onCompleted: () => handleClose(),
	})

	const [updateQuestion, { loading: updating_question }] = useMutation(UPDATE_ADDITIONAL_QUESTION, {
		onCompleted: () => handleClose(),
	})

	const loading = creating_question || updating_question

	const handleSave = () => {
		let new_errors = []

		if (question.question.length === 0) new_errors = ['question']

		if (!question.freeform_response && question.options.length === 0) new_errors = [...new_errors, 'options']

		if (new_errors.length > 0) {
			setError(new_errors)

			return
		}

		const payload = { ...question }

		delete payload.freeform_response

		if (is_editing) {
			updateQuestion({ variables: payload })
		} else {
			createQuestion({ variables: { ...payload, organization_id } })
		}
	}

	const toggleError = (field) => {
		if (!error.includes(field)) return

		setError((prev) => prev.filter((e) => e !== field))
	}

	return (
		<Modal show modalDidClose={handleClose} style={{ width: 'auto' }}>
			<div>
				<h3>{is_editing ? 'Edit' : 'Create'} Additional Question</h3>

				<QuestionField className={`${error.includes('question') ? 'error' : ''}`}>
					<span className="label">Question</span>
					<div className="field">
						<input
							value={question.question || ''}
							name="new_default_campaign_question"
							className="form-control"
							placeholder="Type New Question"
							onChange={(e) => {
								toggleError('question')
								setQuestion({ ...question, question: e.target.value })
							}}
							disabled={loading}
							autoFocus
						/>

						<FontAwesomeIcon icon="exclamation-triangle" className="error-icon text-danger ml-2" />
					</div>
				</QuestionField>

				<div className="d-flex">
					<ResponsesField className={`${error.includes('options') ? 'error' : ''}`}>
						<span className="label options">Response Options</span>

						<ResponsesSelector
							question={question}
							onEditQuestion={(new_options, new_freeform = question.freeform_response) => {
								toggleError('options')
								setQuestion({
									...question,
									options: new_options,
									freeform_response: new_freeform,
								})
							}}
						/>
					</ResponsesField>

					<Divider />

					<TopicsField>
						<span className="label topics">Topics</span>

						<TopicSelector
							question={question}
							onEditQuestion={(new_topics, new_global = question.global) =>
								setQuestion({ ...question, topics: new_topics, global: new_global })
							}
						/>
					</TopicsField>
				</div>

				<button onClick={handleClose} className="btn btn-danger mt-4">
					Cancel
				</button>

				<button onClick={handleSave} className="btn btn-success float-right mt-4">
					{is_editing ? 'Save Changes' : 'Create Additional Question'}
				</button>
			</div>
		</Modal>
	)
}

AdditionalQuestionForm.propTypes = {
	additional_question: PropTypes.object,
	organization_id: PropTypes.array,
	handleClose: PropTypes.func,
}

export default AdditionalQuestionForm
